import { CustomUploader } from './custom_uploader'
import { withCameraCaptureTime } from './capture_time'
import I18n from './i18n.js'
import createPica from 'pica'

let imageBlobReducePromise

const loadImageBlobReduce = function() {
  if (!imageBlobReducePromise) {
    imageBlobReducePromise = import('image-blob-reduce')
      .then(module => {
        const createImageBlobReduce = module.default || module
        // pica's web workers break once webpack minifies the ESM build
        // (`ReferenceError: Can't find variable: i` in Safari, once per
        // tile). That shows up in production packs but not in the
        // unminified dev server. Always resize on the main thread.
        return createImageBlobReduce({
          pica: createPica({ features: ['js', 'wasm'] })
        })
      })
      .catch(() => null)
  }

  return imageBlobReducePromise
}

// We have a file upload field in a div called .file-upload
// 1. When a file gets attached to it, we hide .file-upload and show a
//    progress bar
// 2. When the upload finishes, we
//    - hide the progress bar
//    - add a div .finished-upload, which is a modified copy of
//      .file-upload that shows the file and contains a hidden field with
//      the file params
//    - remove the hidden field from .file-upload
//    - show a link to add another file (if multipe uploads are enabled
//      and there are less than 10 uploads already in this .file-upload-group)
// 3. When the link to add another file is clicked,
//    - we show the original file input again, which was hidden until now
//    - we hide that very same link again
// 4. Repeat from step one, to add more uploads
// 5. When a .finished-upload row is clicked, we remove that upload row
// 6. When the last .finished-upload div gets removed, we show the file
//    upload field again
//
// If the form redisplays, for example because of validation errors, the
// state after step 2 should be displayed (without invoking any JS)

document.addEventListener('turbo:load', () => {
  // Handle change, e.g. User attaches a file
  const inputs = Array.from(document.querySelectorAll('input.file-upload-input[type="file"]')) // Array.from needed as a Tribute to IE11

  // Bind to file selection
  if (inputs.length > 0) {
    inputs.forEach(input => {
      addUploadEventListener(input)
    })
  }

  // Handle reset
  const customFileGroupsFinished = Array.from(document.querySelectorAll('.file-upload.finished-upload'))
  if (customFileGroupsFinished.length > 0) {
    customFileGroupsFinished.forEach(customFileGroup => {
      customFileGroup.addEventListener('click', resetUploadField)
    })
  }

  // Handle "Add another upload" link
  const addUploadLinks = Array.from(document.querySelectorAll('.add-upload-link a'))
  if (addUploadLinks.length > 0) {
    addUploadLinks.forEach(addUploadLink => {
      addUploadLink.addEventListener('click', event => {
        event.preventDefault()

        const formGroup = addUploadLink.closest('.file-upload-group')
        if (!formGroup) {
          return
        }

        const originalUploadField = formGroup.querySelector('.file-upload.original')
        if (originalUploadField) {
          originalUploadField.classList.remove('d-none')
        }

        const addUploadContainer = addUploadLink.closest('.add-upload-link')
        if (addUploadContainer) {
          addUploadContainer.classList.add('invisible')
        }
      })
    })
  }
})

addEventListener('direct-upload:initialize', event => {
  const { target, detail } = event
  const { id, file } = detail

  const customFileGroup = target.parentNode

  customFileGroup.classList.add('d-none')

  customFileGroup.insertAdjacentHTML('beforebegin', `
    <div id="direct-upload-${id}" class="direct-upload direct-upload--pending">
      <div id="direct-upload-progress-${id}" class="direct-upload__progress" style="width: 0%"></div>
      <span class="direct-upload__filename">${file.name}</span>
    </div>
  `)
})

addEventListener('direct-upload:start', event => {
  const { id, file } = event.detail
  const element = document.getElementById(`direct-upload-${id}`)
  element.classList.remove('direct-upload--pending')

  // Disable submit button - handle both create and update forms
  const submitButton = document.querySelector('button.crud_action_create') || document.querySelector('button.crud_action_update')
  if (submitButton) {
    submitButton.disabled = true
  }

  // Add preview image
  element.insertAdjacentHTML('afterbegin', '<div class="upload-preview"></div>')
  previewImage(file, element)
})

addEventListener('direct-upload:progress', event => {
  const { id, progress } = event.detail
  const progressElement = document.getElementById(`direct-upload-progress-${id}`)
  progressElement.style.width = `${progress}%`
})

addEventListener('direct-upload:error', event => {
  event.preventDefault()
  const { id, error } = event.detail
  const element = document.getElementById(`direct-upload-${id}`)
  element.classList.add('direct-upload--error')
  element.setAttribute('title', error)
})

addEventListener('direct-upload:end', event => {
  const { target, detail } = event
  const { id, file } = detail
  const progress = document.getElementById(`direct-upload-${id}`)
  const uploadFileGroup = target.parentNode
  const formGroup = uploadFileGroup.parentNode
  const addUploadLink = formGroup.querySelector('.add-upload-link')

  // Enable submit button - handle both create and update forms
  const submitButton = document.querySelector('button.crud_action_create') || document.querySelector('button.crud_action_update')
  if (submitButton) {
    submitButton.disabled = false
  }

  // Hide progress bar
  progress.parentNode.removeChild(progress)

  // Show finished-upload
  const finishedUploadGroup = uploadFileGroup.cloneNode(true)
  finishedUploadGroup.classList.add('finished-upload')
  // Add preview image
  finishedUploadGroup.querySelector('.file-upload-label').insertAdjacentHTML('afterbegin', '<div class="upload-preview"></div>')
  previewImage(file, finishedUploadGroup)
  // Display and insert that shit
  finishedUploadGroup.classList.remove('d-none')
  finishedUploadGroup.classList.remove('original')
  uploadFileGroup.parentNode.insertBefore(finishedUploadGroup, uploadFileGroup)

  // Remove hidden field from uploadFileGroup
  const hiddenInput = uploadFileGroup.querySelector('.cache')
  if (hiddenInput) {
    hiddenInput.parentNode.removeChild(hiddenInput)
  }

  // Show "Add another upload" link if there are not more then 10 uploads
  // already attached
  if (addUploadLink && formGroup.querySelectorAll('.file-upload').length <= 11) {
    addUploadLink.classList.remove('invisible')
  }

  // Reset file field label
  const placeholderText = uploadFileGroup.querySelector('.file-upload-input').dataset.fallbackPlaceholder
  uploadFileGroup.querySelector('.file-upload-label').innerHTML = placeholderText

  // Handle file field reset
  finishedUploadGroup.addEventListener('click', resetUploadField, { once: true })
})

function resetUploadField(event) {
  event.preventDefault()
  const customFileGroup = event.target.closest('.file-upload')
  if (!customFileGroup) {
    return
  }
  const formGroup = customFileGroup.parentNode

  // Remove hidden input
  const hiddenInput = customFileGroup.querySelector('.cache')
  if (hiddenInput) {
    hiddenInput.parentNode.removeChild(hiddenInput)
  }

  // Remove input if clone or reset, if original
  if (customFileGroup.classList.contains('finished-upload')) {
    customFileGroup.parentNode.removeChild(customFileGroup)
  }

  if (formGroup.querySelectorAll('.file-upload').length <= 1) {
    // Display hidden original .file-upload
    const originalCustomFileGroup = formGroup.querySelector('.file-upload.original')
    originalCustomFileGroup.classList.remove('d-none')
    // Restore label text
    const placeholderText = originalCustomFileGroup.querySelector('.file-upload-input').dataset.fallbackPlaceholder
    originalCustomFileGroup.querySelector('.file-upload-label').innerHTML = placeholderText
    // Hide "Add another upload" link
    const addUploadLink = formGroup.querySelector('.add-upload-link')
    if (addUploadLink) {
      addUploadLink.classList.add('invisible')
    }
  }
}

function addUploadEventListener(input) {
  input.addEventListener('change', event => {
    const { target } = event
    const label = target.nextElementSibling

    // Set filename on label
    const firstFile = target.files && target.files[0]
    const fileName = firstFile ? firstFile.name : target.value.split('\\').pop()
    if (label) {
      label.classList.add('selected')
      label.innerHTML = fileName
    }

    const passToUpload = (input, file) => {
      const uploader = new CustomUploader(input, file)
      uploader.start(file)
    }

    Array.from(input.files).forEach(file => {
      if (isInFileNameBlackList(file.name)) {
        alert(I18n.t('errors.file_invalid'))
      } else if (isImageFile(file)) {
        // A photo from iOS Safari's camera gets the capture time WebKit drops
        const uploadImage = (input, blob) => {
          withCameraCaptureTime(file, blob).then(stamped => passToUpload(input, stamped))
        }

        // Check if the file is an image, attempt to validate it and then compress it.
        const validateImage = (file) => {
          return new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve(true) // Image is valid
            img.onerror = () => reject(new Error('Image is corrupted or invalid')) // Image is corrupted or invalid

            const url = URL.createObjectURL(file)
            img.src = url
          })
        }

        validateImage(file)
          .then(isValid => {
            // Rails writes `compression: false` out as "false", which is truthy.
            // A HEIC goes up as it is: the browser would re-encode it as a
            // JPEG without its EXIF data, and with it the capture time the
            // photo stamps show (PhotoTimestamp). The server makes the JPEG
            // to show it by.
            if (isValid && input.dataset.compression === 'true' && !isHeicFile(file)) {
              // Image is valid, proceed with resizing and compression
              const config = {
                max: 1500,
                unsharpAmount: 80,
                unsharpRadius: 0.6,
                unsharpThreshold: 2
              }

              loadImageBlobReduce().then(reducer => {
                if (!reducer) {
                  uploadOriginalIfProcessable(input, file, uploadImage)
                  return
                }

                reducer
                  .toBlob(file, config)
                  .then(imageBlob => {
                    imageBlob.lastModifiedDate = new Date()
                    imageBlob.name = file.name.replace(
                      /\.(heic|heif|dng)$/i,
                      extensionForImageBlob(imageBlob)
                    )

                    uploadImage(input, imageBlob)
                  })
                  .catch(() => {
                    uploadOriginalIfProcessable(input, file, uploadImage)
                  })
              })
            } else {
              uploadOriginalIfProcessable(input, file, uploadImage)
            }
          })
          .catch(() => {
            if (isWebDecodableImageType(file)) {
              alert(I18n.t('errors.file_corrupted'))
            } else {
              uploadOriginalIfProcessable(input, file, uploadImage)
            }
          })
      } else {
        passToUpload(input, file)
      }
    })
    // you might clear the selected files from the input
    input.value = null
    if (label) {
      label.innerHTML = fileName // IE11 wil end up with empty label otherwise …
    }
  })
}

// A thumbnail for an image. Anything else, a PDF report or an Office file,
// shows its type, as the server-rendered preview does: an <img> cannot show a
// PDF and would only draw a broken image.
function previewImage(file, container) {
  const preview = container.querySelector('.upload-preview')

  if (!/^image\//.test(file.type)) {
    preview.textContent = file.type.split('/').pop()
    return
  }

  const img = document.createElement('img')
  img.classList.add('obj')
  img.file = file
  preview.appendChild(img)

  const reader = new FileReader()
  reader.onload = (function(aImg) { return function(e) { aImg.src = e.target.result } })(img)
  reader.readAsDataURL(file)
}

function isImageFile(file) {
  if (file.type && file.type.startsWith('image/')) {
    return true
  }

  return /\.(jpe?g|png|gif|webp|heic|heif|dng)$/i.test(file.name)
}

// An iPhone photo, which iOS Safari passes on as it is, the input accepting
// HEIC (Uploads::Files::HeicTypes)
function isHeicFile(file) {
  return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name)
}

function isWebDecodableImageType(file) {
  return /image\/(jpeg|pjpeg|jpg|png|gif|webp)/i.test(file.type)
}

// Adobe DNG / iPhone ProRAW. libvips has no DNG loader (heic/heif only),
// so the original file cannot be stored — only a browser-transcoded JPEG/PNG.
function isDngFile(file) {
  return /\.dng$/i.test(file.name) || /image\/(dng|x-adobe-dng|x-raw-adobe)/i.test(file.type)
}

function extensionForImageBlob(blob) {
  switch (blob.type) {
    case 'image/png':
      return '.png'
    case 'image/webp':
      return '.webp'
    case 'image/gif':
      return '.gif'
    default:
      return '.jpg'
  }
}

function uploadOriginalIfProcessable(input, file, passToUpload) {
  if (isDngFile(file)) {
    alert(I18n.t('errors.file_invalid'))
    return
  }

  passToUpload(input, file)
}

function isInFileNameBlackList(fileName) {
  // Match any .ico file
  return /\.ico$/i.test(fileName)
}
